import * as Dialog from '@radix-ui/react-dialog'
import * as RadixSelect from '@radix-ui/react-select'
import { CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, LoaderCircle, X } from 'lucide-react'
import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; loading?: boolean }>(
  ({ className, variant = 'primary', loading, children, disabled, ...props }, ref) => (
    <button ref={ref} className={cn('button', `button-${variant}`, className)} disabled={disabled || loading} {...props}>
      {loading && <LoaderCircle size={16} className="spin" />}{children}
    </button>
  )
)
Button.displayName = 'Button'

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card', className)} {...props} />
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input ref={ref} className={cn('input', className)} {...props} />
))
Input.displayName = 'Input'

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn('input textarea', className)} {...props} />
))
Textarea.displayName = 'Textarea'

export type SelectOption = { value: string; label: string; disabled?: boolean }

/** 基于 Radix Select 的自定义下拉组件，替代原生 <select> 以获得完整的暗色主题渲染。 */
export function Select({ value, onValueChange, options, placeholder, disabled, className }: {
  value: string
  onValueChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  className?: string
}) {
  return <RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
    <RadixSelect.Trigger className={cn('select-trigger', className)} aria-label={placeholder}>
      <RadixSelect.Value placeholder={placeholder || '请选择…'}/>
      <RadixSelect.Icon className="select-icon"><ChevronDown size={15}/></RadixSelect.Icon>
    </RadixSelect.Trigger>
    <RadixSelect.Portal>
      <RadixSelect.Content className="select-content" position="popper" sideOffset={6} align="start">
        <RadixSelect.ScrollUpButton className="select-scroll-btn"><ChevronUp size={14}/></RadixSelect.ScrollUpButton>
        <RadixSelect.Viewport className="select-viewport">
          {options.map(option => (
            <RadixSelect.Item key={option.value} value={option.value} disabled={option.disabled} className="select-item">
              <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              <RadixSelect.ItemIndicator className="select-indicator"><Check size={13}/></RadixSelect.ItemIndicator>
            </RadixSelect.Item>
          ))}
        </RadixSelect.Viewport>
        <RadixSelect.ScrollDownButton className="select-scroll-btn"><ChevronDown size={14}/></RadixSelect.ScrollDownButton>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  </RadixSelect.Root>
}

/** 数字输入框：仅允许输入数字，decimal 模式下额外允许一个小数点 */
export const NumberInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { decimal?: boolean }>(
  ({ className, decimal, onChange, ...props }, ref) => (
    <input
      ref={ref}
      className={cn('input', className)}
      inputMode={decimal ? 'decimal' : 'numeric'}
      onChange={e => {
        const raw = e.target.value
        const cleaned = decimal
          ? raw.replace(/[^\d.]/g, '').split('.').slice(0, 2).join('.')
          : raw.replace(/\D/g, '')
        if (cleaned !== raw) e.target.value = cleaned
        onChange?.(e)
      }}
      {...props}
    />
  )
)
NumberInput.displayName = 'NumberInput'

const WEEKDAYS = ['一', '二', '三', '四', '五', '六', '日']
const pad2 = (n: number) => String(n).padStart(2, '0')
const toDateString = (year: number, month: number, day: number) => `${year}-${pad2(month + 1)}-${pad2(day)}`

/** 自定义日历面板。原生 date input 的弹出层由浏览器渲染、无法统一暗色风格，
 *  且 Modal 容器带 transform 会破坏 fixed 定位，因此面板通过 Portal 挂到 body。 */
function DatePicker({ date, onSelect, required }: { date: string; onSelect: (date: string) => void; required?: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const hasDate = /^\d{4}-\d{2}-\d{2}$/.test(date) && !Number.isNaN(new Date(`${date}T00:00`).getTime())
  const parsed = hasDate ? date.split('-').map(Number) : []
  const selYear = hasDate ? parsed[0] : 0
  const selMonth = hasDate ? parsed[1] - 1 : -1
  const selDay = hasDate ? parsed[2] : 0
  const now = new Date()
  const [viewYear, setViewYear] = useState(hasDate ? selYear : now.getFullYear())
  const [viewMonth, setViewMonth] = useState(hasDate ? selMonth : now.getMonth())

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onMove = () => setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    document.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  function toggle() {
    if (open) return setOpen(false)
    setViewYear(hasDate ? selYear : now.getFullYear())
    setViewMonth(hasDate ? selMonth : now.getMonth())
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) {
      const panelHeight = 322
      const flipUp = rect.bottom + panelHeight > window.innerHeight - 8 && rect.top - panelHeight > 8
      setPos({ top: flipUp ? rect.top - panelHeight - 6 : rect.bottom + 6, left: Math.max(8, rect.left) })
    }
    setOpen(true)
  }

  function nav(delta: number) {
    let m = viewMonth + delta, y = viewYear
    if (m < 0) { m = 11; y-- } else if (m > 11) { m = 0; y++ }
    setViewYear(y); setViewMonth(m)
  }

  // 生成日历格子（周一开头），首尾补上/下月日期
  const firstDow = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7
  const days = new Date(viewYear, viewMonth + 1, 0).getDate()
  const prevDays = new Date(viewYear, viewMonth, 0).getDate()
  const cells: Array<{ day: number; value: string; outside: boolean }> = []
  for (let i = firstDow - 1; i >= 0; i--) {
    const d = prevDays - i
    cells.push({ day: d, value: toDateString(viewMonth === 0 ? viewYear - 1 : viewYear, viewMonth === 0 ? 11 : viewMonth - 1, d), outside: true })
  }
  for (let d = 1; d <= days; d++) cells.push({ day: d, value: toDateString(viewYear, viewMonth, d), outside: false })
  let next = 1
  while (cells.length % 7 !== 0) {
    cells.push({ day: next, value: toDateString(viewMonth === 11 ? viewYear + 1 : viewYear, viewMonth === 11 ? 0 : viewMonth + 1, next), outside: true })
    next++
  }

  const todayStr = toDateString(now.getFullYear(), now.getMonth(), now.getDate())

  return <div className="date-picker" ref={rootRef}>
    <CalendarDays size={15} className="datetime-icon"/>
    <input
      type="text"
      className={cn('date-picker-input', !hasDate && 'is-empty')}
      value={date}
      placeholder="YYYY-MM-DD"
      inputMode="numeric"
      aria-required={required}
      onChange={e => onSelect(e.target.value)}
      aria-label="日期"
    />
    <button type="button" ref={triggerRef} className="date-picker-trigger" onClick={toggle} aria-haspopup="dialog" aria-expanded={open} aria-label="打开日期选择器">
      <ChevronDown size={14}/>
    </button>
    {open && createPortal(
      <div className="date-picker-panel" ref={panelRef} style={{ top: pos.top, left: pos.left }} role="dialog" aria-label="选择日期">
        <div className="date-picker-head">
          <button type="button" onClick={() => nav(-1)} aria-label="上个月"><ChevronLeft size={15}/></button>
          <strong>{viewYear}年{viewMonth + 1}月</strong>
          <button type="button" onClick={() => nav(1)} aria-label="下个月"><ChevronRight size={15}/></button>
        </div>
        <div className="date-picker-week">{WEEKDAYS.map(w => <span key={w}>{w}</span>)}</div>
        <div className="date-picker-grid">
          {cells.map(cell => <button
            type="button"
            key={cell.value}
            className={cn('date-picker-day', cell.outside && 'is-outside', cell.value === todayStr && 'is-today', cell.value === date && 'is-selected')}
            onClick={() => { onSelect(cell.value); setOpen(false) }}
          >{cell.day}</button>)}
        </div>
      </div>,
      document.body,
    )}
  </div>
}

/** 日期时间输入框：拆分为日期 + 时间两段，值为 `YYYY-MM-DDTHH:mm` */
export function DateTimeInput({ value, onChange, required }: { value: string; onChange: (value: string) => void; required?: boolean }) {
  const [datePart = '', timePart = ''] = value ? value.split('T') : []
  const emit = (date: string, time: string) => onChange(date ? `${date}T${time || '00:00'}` : '')
  return <div className="datetime-input">
    <DatePicker date={datePart} onSelect={d => emit(d, timePart)} required={required}/>
    <span className="datetime-sep"/>
    <Clock size={15} className="datetime-icon"/>
    <input type="time" className="datetime-time" value={timePart} onChange={e => emit(datePart, e.target.value)} aria-required={required} aria-label="时间"/>
  </div>
}

export function Field({ label, hint, required, children, className }: { label: string; hint?: ReactNode; required?: boolean; children: ReactNode; className?: string }) {
  return <label className={cn('field', className)}><span className="field-label">{label}{required && <i>*</i>}</span>{children}{hint && <small>{hint}</small>}</label>
}

export function Badge({ children, tone = 'blue' }: { children: ReactNode; tone?: 'blue' | 'green' | 'amber' | 'red' | 'neutral' }) {
  return <span className={cn('badge', `badge-${tone}`)}>{children}</span>
}

export function Modal({ open, onOpenChange, title, description, children, className }: { open: boolean; onOpenChange: (open: boolean) => void; title: string; description?: string; children: ReactNode; className?: string }) {
  return <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className="dialog-overlay" />
      <Dialog.Content className={cn('dialog-content', className)}>
        <div className="dialog-heading"><div><Dialog.Title>{title}</Dialog.Title>{description && <Dialog.Description>{description}</Dialog.Description>}</div><Dialog.Close className="icon-button" aria-label="关闭弹窗"><X size={18} /></Dialog.Close></div>
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
}

export function PageTitle({ eyebrow, title, description, actions }: { eyebrow: string; title: string; description: string; actions?: ReactNode }) {
  return <div className="page-title"><div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{description}</p></div>{actions && <div className="page-actions">{actions}</div>}</div>
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state"><div className="empty-mark">M</div><h3>{title}</h3><p>{description}</p>{action}</div>
}
